import { useCreateContextHook } from 'contexts/useCreateContextHook';
import React, { createContext } from 'react';
import { useAppConfig } from '@landr/mfe';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';

export type AppSettingsContextType = {
    isExtendableHeight: boolean;
    isFooterAllowed: boolean;
    showFeaturedContent: boolean;
    showLandrPlugins: boolean;
    hasCustomSeo: boolean;
};

export const defaultAppSettingsContext: AppSettingsContextType = {
    isExtendableHeight: true,
    isFooterAllowed: true,
    showFeaturedContent: true,
    showLandrPlugins: true,
    hasCustomSeo: true,
};

export const AppSettingsContext = createContext<AppSettingsContextType>(
    defaultAppSettingsContext,
);

export const AppSettingsProvider: React.FC<{
    isExtendableHeight: boolean;
    isFooterAllowed: boolean;
    children: React.ReactNode;
}> = ({ isExtendableHeight, isFooterAllowed, children }) => {
    // Require until app setting endpoint handle guest user
    const { isGuestUser } = useAuthentication();
    const appConfig = useAppConfig();
    const {
        showFeaturedContent: showFeaturedContentAppConfig,
        hasCustomSeo: hasCustomSeoAppConfig,
        showLandrPlugins,
    } = appConfig.products.samples;

    const showFeaturedContent = isGuestUser || showFeaturedContentAppConfig;
    const hasCustomSeo = isGuestUser || hasCustomSeoAppConfig;

    return (
        <AppSettingsContext.Provider
            value={{
                isExtendableHeight,
                isFooterAllowed,
                showFeaturedContent,
                showLandrPlugins,
                hasCustomSeo,
            }}
        >
            {children}
        </AppSettingsContext.Provider>
    );
};

export const useAppSettings = () => {
    const context = useCreateContextHook({
        context: AppSettingsContext,
        errorMessage:
            'useAppSettings must be used within a AppSettingsProvider',
    });

    return context;
};

export default AppSettingsProvider;
